import { useState, CSSProperties } from 'react';
import { invoke } from '@tauri-apps/api/core';
import toast from 'react-hot-toast';
import { environment } from '../../environment/environment';
import { DesktopUpdateState } from '../../hooks/useDesktopUpdates';

// Desktop-only update prompts (polled live by useDesktopUpdates):
//  - Rust app update -> "Relaunch now"  (installs the new build + relaunches)
//  - MCP update      -> "Relaunch app"  (relaunch downloads the new MCP binary;
//                                         user then restarts Claude to load it)
// Non-destructive: nothing restarts until the user clicks.
export default function DesktopUpdateBanner({ rustUpdate, mcpUpdate, mcpPendingRestart }: DesktopUpdateState) {
  const [busy, setBusy] = useState<'rust' | 'mcp' | 'mcp-restart' | null>(null);
  const [dismissedMcp, setDismissedMcp] = useState(false);
  const [dismissedRestart, setDismissedRestart] = useState(false);

  const relaunchForRust = async () => {
    setBusy('rust');
    const base = environment.skillsBaseUrl.replace(/\/+$/, '');
    toast.loading(`Updating to v${rustUpdate}… the app will relaunch`, { id: 'app-update', duration: 60000 });
    try {
      await invoke('check_and_install_update', { endpoint: `${base}/updates/latest.json` });
    } catch {
      toast.dismiss('app-update');
      setBusy(null);
    }
  };

  const relaunchForMcp = async () => {
    setBusy('mcp');
    try { await invoke('relaunch_app'); } catch { setBusy(null); }
  };

  const restartClaude = async () => {
    setBusy('mcp-restart');
    try { await invoke('restart_claude'); setDismissedRestart(true); } catch { setBusy(null); }
  };

  const showRust = !!rustUpdate;
  // A Rust relaunch also downloads the new MCP, so only show the MCP prompt when
  // there's no Rust update pending (avoids two "relaunch" prompts at once).
  const showMcp = mcpUpdate && !showRust && !dismissedMcp;
  const showRestart = mcpPendingRestart && !dismissedRestart;
  if (!showRust && !showMcp && !showRestart) return null;

  return (
    <div style={wrap}>
      {showRust && (
        <div style={card}>
          <div style={title}>New version available</div>
          <div style={body}>Atlas v{rustUpdate} is ready. Relaunch to update.</div>
          <button style={primary} disabled={busy === 'rust'} onClick={relaunchForRust}>
            {busy === 'rust' ? 'Updating…' : 'Relaunch now'}
          </button>
        </div>
      )}
      {showMcp && (
        <div style={card}>
          <div style={title}>Atlas tools updated</div>
          <div style={body}>Relaunch Atlas to apply the latest tools, then restart Claude.</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={primary} disabled={busy === 'mcp'} onClick={relaunchForMcp}>
              {busy === 'mcp' ? 'Relaunching…' : 'Relaunch app'}
            </button>
            <button style={secondary} onClick={() => setDismissedMcp(true)}>Later</button>
          </div>
        </div>
      )}
      {showRestart && (
        <div style={card}>
          <div style={title}>MCP updated</div>
          <div style={body}>Restart Claude to load the latest Atlas tools.</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={primary} disabled={busy === 'mcp-restart'} onClick={restartClaude}>
              {busy === 'mcp-restart' ? 'Restarting…' : 'Restart Claude'}
            </button>
            <button style={secondary} onClick={() => setDismissedRestart(true)}>Later</button>
          </div>
        </div>
      )}
    </div>
  );
}

// top offset clears the custom title bar (36px) — this banner is desktop-only.
const wrap: CSSProperties = { position: 'fixed', top: 48, right: 12, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 8 };
const card: CSSProperties = {
  maxWidth: 320, background: '#fff', border: '1px solid #E1E6EC', borderRadius: 10,
  boxShadow: '0 4px 12px rgba(14,46,92,0.12)', padding: '12px 14px',
  font: '400 13px/1.4 "Work Sans", sans-serif', color: '#0A1F3D',
};
const title: CSSProperties = { fontWeight: 700, marginBottom: 4 };
const body: CSSProperties = { color: '#364759', marginBottom: 10 };
const primary: CSSProperties = { background: '#0E2E5C', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', fontWeight: 600, cursor: 'pointer' };
const secondary: CSSProperties = { background: 'transparent', color: '#5A6A7E', border: '1px solid #C9D1DC', borderRadius: 6, padding: '6px 12px', cursor: 'pointer' };
